"use client";

import type { ReactNode } from "react";
import { useId, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Calendar,
  Eye,
  FileText,
  Linkedin,
  ListChecks,
  Mail,
  MessageCircle,
  MessageSquare,
  Share2,
  Trash2,
  User,
  Users,
  Wrench,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

/** Bezier from hub (center) to each integration tile, viewBox 0 0 400 200, hub at ~ (200, 100) */
const LINE_PATHS: Record<"L0" | "L1" | "L2" | "R0" | "R1" | "R2", string> = {
  L0: "M 200 100 C 170 100 120 50 100 34",
  L1: "M 200 100 C 165 100 100 100 100 100",
  L2: "M 200 100 C 170 100 120 150 100 168",
  R0: "M 200 100 C 230 100 280 50 300 34",
  R1: "M 200 100 C 235 100 300 100 300 100",
  R2: "M 200 100 C 230 100 280 150 300 168",
};

const HINTS: Record<string, string> = {
  hub: "Your organization subscribes to inaraX; we host the course we built for you and connect your stack so admins see the same data learners earn in lessons and quizzes.",
  "metric-0":
    "Seats & subscription: how many you have under contract, who is provisioned, and who is invited into the org’s course on inaraX.",
  "metric-1":
    "Engagement: learners currently active in your program—so you can staff support, run nudges, and hit deadlines before a quiz or milestone closes.",
  "metric-2":
    "Quizzes & checks: how many attempts, scores, and pass rates for the assessments tied to the course inaraX generated for you.",
  "metric-3":
    "Progress through the course: lessons and modules your people have completed, mapped to the path your org subscribed to.",
  "int-L0":
    "Onboarding and renewals: email for invites, re-invites, and subscription lifecycle so no seat sits idle and users know their course is live.",
  "int-L1":
    "Nudges in Slack or Teams: quiz windows, course milestones, and manager prompts—without a separate comms system for the program.",
  "int-L2":
    "IdP/SSO and HR: seat claims tied to who works at your org so the right people access your subscription’s course, and the wrong accounts never appear in your dashboard.",
  "int-R0":
    "Schedule: cohort kickoffs, review sessions, and quiz deadlines on the org calendar you already use.",
  "int-R1":
    "Show completion and skills: share proof that your subscription delivered—quizzes passed, path done—on LinkedIn and internal talent systems.",
  "int-R2":
    "Executives and procurement: export progress and score summaries from the same dashboard you use to run the day-to-day program.",
  "user-0":
    "Program owner: contract owner for the subscription, scope of the custom course, and which leaders get dashboard and export access.",
  "user-1":
    "L&D or people partner: day-to-day in the product—nudges, quiz readiness, and reading the org dashboard to improve completion.",
  "user-2":
    "Learner: works through the course inaraX built for your org—lessons, placement where needed, quizzes, and their own progress view inside the app.",
  enter:
    "Open the org’s course experience: the learner app where your subscription’s content, quizzes, and progress live in one place.",
  "sidebar-0":
    "Admin configuration: which groups get the course, required quizzes, and what appears on your org’s monitoring dashboard.",
  "sidebar-1":
    "In-product comms: announce schedule changes, quiz extensions, and support without losing context outside inaraX.",
  "sidebar-2":
    "Delegate view-only dashboard links to a sponsor or function head who does not need full admin rights.",
  "sidebar-3":
    "Offboard cleanly: when someone leaves, release the seat, keep a record of completion and scores for the org’s audit trail.",
};

type B2BHeroHubDiagramProps = {
  className?: string;
};

type FocusKey =
  | "hub"
  | "metric-0"
  | "metric-1"
  | "metric-2"
  | "metric-3"
  | "int-L0"
  | "int-L1"
  | "int-L2"
  | "int-R0"
  | "int-R1"
  | "int-R2"
  | "user-0"
  | "user-1"
  | "user-2"
  | "enter"
  | "sidebar-0"
  | "sidebar-1"
  | "sidebar-2"
  | "sidebar-3";

function InaraXIconChip({ className = "" }: { className?: string }) {
  return (
    <div
      className={["flex h-8 w-8 items-center justify-center rounded-full bg-stone-900 text-sm font-extrabold text-white", className].filter(Boolean).join(" ")}
      aria-hidden
    >
      X
    </div>
  );
}

function MetricListItem({
  icon: Icon,
  label,
  value,
  active,
  iconClass,
  focusKey,
  focusId,
  onFocusKey,
  reduce,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  active?: boolean;
  iconClass: string;
  focusKey: FocusKey;
  focusId: FocusKey | null;
  onFocusKey: (k: FocusKey) => void;
  reduce: boolean;
}) {
  const isH = focusId === focusKey;
  return (
    <motion.button
      type="button"
      onMouseEnter={() => onFocusKey(focusKey)}
      onFocus={() => onFocusKey(focusKey)}
      whileHover={reduce ? undefined : { y: -1 }}
      whileTap={reduce ? undefined : { scale: 0.99 }}
      className={[
        "w-full text-left",
        "flex items-start gap-2 rounded-2xl border p-1.5 transition-[box-shadow,background-color,border-color] duration-200",
        "outline-none focus-visible:ring-2 focus-visible:ring-stone-900/25 focus-visible:ring-offset-1",
        active || isH ? "border-stone-300/80 bg-white shadow-md shadow-stone-900/8 ring-1 ring-stone-200/80" : "border-transparent hover:border-stone-200/60 hover:bg-white/50",
        isH && !active ? "ring-1 ring-stone-300/90" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={["mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg", iconClass].join(" ")}>
        <Icon className="h-3.5 w-3.5 text-white" strokeWidth={2} />
      </div>
      <div className="min-w-0 text-left text-[10px] leading-tight sm:text-xs">
        <p className="font-semibold text-stone-800">{label}</p>
        <p className="text-stone-500">{value}</p>
      </div>
    </motion.button>
  );
}

function IntegrationTile({
  focusKey,
  focusId,
  onFocusKey,
  reduce,
  title,
  children,
  className = "",
  delay = 0,
}: {
  focusKey: FocusKey;
  focusId: FocusKey | null;
  onFocusKey: (k: FocusKey) => void;
  reduce: boolean;
  title: string;
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const isH = focusId === focusKey;
  return (
    <motion.button
      type="button"
      title={title}
      onMouseEnter={() => onFocusKey(focusKey)}
      onFocus={() => onFocusKey(focusKey)}
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease }}
      whileHover={reduce ? undefined : { scale: 1.08, y: -2 }}
      whileTap={reduce ? undefined : { scale: 0.95 }}
      className={[
        "z-20 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-2xl border bg-white shadow-[0_6px_24px_rgba(15,23,42,0.08)] ring-1 transition-[box-shadow,ring-color,transform] duration-200",
        "outline-none focus-visible:ring-2 focus-visible:ring-[#0050EF]/40 focus-visible:ring-offset-2",
        isH ? "border-stone-300 shadow-lg ring-2 ring-[#0050EF]/35" : "border-stone-200/90 ring-stone-100/90",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </motion.button>
  );
}

/**
 * BrownRain-style hub: inaraX in the center, integration tiles and curved
 * connection lines, flanked by dashboard and team admin mock cards.
 * Interactive: hover or focus to highlight the matching path and a short hint.
 */
export default function B2BHeroHubDiagram({ className = "" }: B2BHeroHubDiagramProps) {
  const reduce = useReducedMotion() ?? false;
  const [focusId, setFocusId] = useState<FocusKey | null>(null);
  const hintId = useId();
  const hint =
    focusId && HINTS[focusId]
      ? HINTS[focusId]
      : "This map is your org’s story: subscription and dashboard on the left, inaraX in the center, and who runs the program vs who takes the course and quizzes on the right. Hover or focus any part.";

  const lineForKey = (k: FocusKey | null) => {
    if (!k) return null;
    if (k.startsWith("int-L") || k.startsWith("int-R")) {
      const m = k.replace("int-", "");
      if (m in LINE_PATHS) return m as keyof typeof LINE_PATHS;
    }
    return null;
  };

  const activeLine = lineForKey(focusId);

  return (
    <div
      className={["w-full", className].filter(Boolean).join(" ")}
      role="region"
      aria-label="Map of organization subscription, inaraX platform, and roles for the custom course, dashboard, and learners"
    >
      <div
        className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-12 lg:items-center lg:gap-3"
        onMouseLeave={() => setFocusId(null)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusId(null);
        }}
      >
        {/* — Left: dashboard — */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease }}
          className="rounded-[1.5rem] border border-stone-200/80 bg-stone-200/40 p-2.5 shadow-sm shadow-stone-900/5 sm:p-3 lg:col-span-4"
        >
          <p className="mb-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-stone-500 sm:mb-2 sm:text-left">
            Org · subscription & dashboard
          </p>
          <div className="grid min-h-[200px] grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3">
            <motion.div
              whileHover={reduce ? undefined : { scale: 1.01 }}
              transition={{ type: "spring", stiffness: 400, damping: 28 }}
              className="flex flex-col rounded-2xl border border-stone-200/90 bg-stone-50/90 p-2 shadow-inner"
            >
              <div className="mb-1.5 flex items-center gap-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />
                <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />
                <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />
              </div>
              <div className="h-1 w-1/2 rounded bg-stone-200" />
              <div className="mt-1 h-0.5 w-3/4 rounded bg-stone-200/80" />
              <div className="mt-2 flex items-center justify-between gap-1">
                <div className="h-6 w-6 shrink-0 rounded-full border border-stone-200 bg-gradient-to-br from-amber-100 to-amber-200" />
                <InaraXIconChip className="h-7 w-7" />
              </div>
              <div className="mt-2 flex justify-center gap-0.5">
                <div className="h-0.5 w-1.5 rounded-full bg-stone-300" />
                <div className="h-0.5 w-1.5 rounded-full bg-stone-400" />
                <div className="h-0.5 w-1.5 rounded-full bg-stone-300" />
              </div>
            </motion.div>
            <div className="space-y-1.5 p-0.5">
              <MetricListItem
                focusKey="metric-0"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                icon={Users}
                label="Seats"
                value="240"
                iconClass="bg-rose-500"
              />
              <MetricListItem
                focusKey="metric-1"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                icon={Eye}
                label="In course now"
                value="611"
                active
                iconClass="bg-sky-500"
              />
              <MetricListItem
                focusKey="metric-2"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                icon={ListChecks}
                label="Quiz results"
                value="1.1k"
                iconClass="bg-amber-500"
              />
              <MetricListItem
                focusKey="metric-3"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                icon={BookOpen}
                label="Lessons done"
                value="12.4k"
                iconClass="bg-violet-500"
              />
            </div>
          </div>
        </motion.div>

        {/* — Center: hub + lines + float icons — */}
        <div className="relative min-h-[200px] lg:col-span-4">
          <p className="z-20 mb-2 text-center text-[10px] font-semibold uppercase tracking-wider text-stone-500 sm:mb-1">
            Platform, course & delivery
          </p>
          <div className="absolute inset-0 z-0 hidden sm:block" aria-hidden>
            <svg className="h-full w-full" viewBox="0 0 400 200" fill="none" preserveAspectRatio="xMidYMid meet">
              {(Object.keys(LINE_PATHS) as (keyof typeof LINE_PATHS)[]).map((id) => (
                <path
                  key={id}
                  d={LINE_PATHS[id]}
                  stroke={activeLine === id ? "#0050EF" : "rgb(214 211 209)"}
                  strokeWidth={activeLine === id ? 2.2 : 1.2}
                  strokeLinecap="round"
                  className="transition-[stroke,stroke-width] duration-300 ease-out"
                />
              ))}
            </svg>
          </div>
          <div className="relative z-10 flex h-full min-h-[200px] items-center justify-center gap-1 px-0 sm:gap-1.5 md:gap-2">
            <div className="flex flex-col items-center gap-2.5 sm:gap-3">
              <IntegrationTile
                focusKey="int-L0"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                title="Invitations & email"
                delay={0.05}
              >
                <Mail className="h-4 w-4 text-rose-500 sm:h-5 sm:w-5" strokeWidth={1.8} />
              </IntegrationTile>
              <IntegrationTile
                focusKey="int-L1"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                title="Slack / Teams nudges"
                delay={0.1}
              >
                <MessageCircle className="h-4 w-4 text-violet-500 sm:h-5 sm:w-5" strokeWidth={1.8} />
              </IntegrationTile>
              <IntegrationTile
                focusKey="int-L2"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                title="IdP / SSO"
                delay={0.15}
              >
                <span className="text-[9px] font-black tracking-tight text-emerald-600 sm:text-[10px]">SSO</span>
              </IntegrationTile>
            </div>
            <div className="z-20 flex min-w-0 items-center justify-center px-0.5">
              <motion.button
                type="button"
                onMouseEnter={() => setFocusId("hub")}
                onFocus={() => setFocusId("hub")}
                whileHover={reduce ? undefined : { scale: 1.06 }}
                whileTap={reduce ? undefined : { scale: 0.98 }}
                className={[
                  "whitespace-nowrap rounded-full border px-3 py-2 text-xs font-bold shadow-lg sm:px-4 sm:py-2.5 sm:text-sm",
                  "outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-[#0050EF]/50 focus-visible:ring-offset-2",
                  "border-stone-900/90 bg-stone-900 text-white",
                  focusId === "hub" ? "ring-2 ring-[#0050EF]/50 ring-offset-2 ring-offset-[#F5F5F1] shadow-stone-900/25" : "shadow-stone-900/20",
                ].join(" ")}
              >
                <span className="font-semibold tracking-tight">inara</span>
                <span className="ml-0.5 font-extrabold">X</span>
              </motion.button>
            </div>
            <div className="flex flex-col items-center gap-2.5 sm:gap-3">
              <IntegrationTile
                focusKey="int-R0"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                title="Cohort calendar"
                delay={0.18}
              >
                <Calendar className="h-4 w-4 text-sky-500 sm:h-5 sm:w-5" strokeWidth={1.8} />
              </IntegrationTile>
              <IntegrationTile
                focusKey="int-R1"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                title="Skills & sharing"
                delay={0.22}
              >
                <Linkedin className="h-4 w-4 text-sky-600 sm:h-5 sm:w-5" strokeWidth={1.8} />
              </IntegrationTile>
              <IntegrationTile
                focusKey="int-R2"
                focusId={focusId}
                onFocusKey={setFocusId}
                reduce={reduce}
                title="Reports & exports"
                delay={0.26}
              >
                <FileText className="h-4 w-4 text-emerald-600 sm:h-5 sm:w-5" strokeWidth={1.8} />
              </IntegrationTile>
            </div>
          </div>
        </div>

        {/* — Right: team / roles — */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08, ease }}
          className="rounded-[1.5rem] border border-stone-200/80 bg-stone-200/40 p-2.5 shadow-sm shadow-stone-900/5 sm:p-3 lg:col-span-4"
        >
          <p className="mb-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-stone-500 sm:mb-2 sm:text-left">
            Who runs the program & who takes the course
          </p>
          <div className="grid min-h-[200px] grid-cols-1 gap-2 sm:grid-cols-[2.2rem,1fr] sm:gap-2">
            <div className="hidden flex-col items-center gap-2 border-r border-stone-300/60 pr-1 sm:flex">
              {(
                [
                  { key: "sidebar-0" as const, label: "Org settings, paths, required quizzes", icon: Wrench },
                  { key: "sidebar-1" as const, label: "In-app course messages", icon: MessageSquare },
                  { key: "sidebar-2" as const, label: "Share progress with stakeholders", icon: Share2 },
                  { key: "sidebar-3" as const, label: "Deprovision and free a seat", icon: Trash2 },
                ] as const
              ).map(({ key, label, icon: SIcon }) => {
                const isActive = focusId === key;
                const isDefaultChat = key === "sidebar-1" && focusId === null;
                return (
                <motion.button
                  key={key}
                  type="button"
                  onMouseEnter={() => setFocusId(key)}
                  onFocus={() => setFocusId(key)}
                  whileTap={reduce ? undefined : { scale: 0.94 }}
                  className={[
                    "flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
                    isActive
                      ? "bg-white text-stone-900 shadow ring-1 ring-[#0050EF]/30"
                      : isDefaultChat
                        ? "bg-white text-stone-800 shadow ring-1 ring-stone-200/90"
                        : "text-stone-500 hover:bg-stone-100/80",
                    "outline-none focus-visible:ring-2 focus-visible:ring-[#0050EF]/40",
                  ].join(" ")}
                  aria-label={label}
                >
                  <SIcon className="h-3.5 w-3.5" />
                </motion.button>
                );
              })}
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              {(
                [
                  { name: "Program owner", role: "Subscription & dashboard access" },
                  { name: "L&D / partner", role: "Nudges, quiz readiness, reports" },
                  { name: "Learner", role: "Your org’s course & quizzes" },
                ] as const
              ).map((u, i) => {
                const fk = `user-${i}` as FocusKey;
                return (
                  <motion.button
                    key={u.name}
                    type="button"
                    onMouseEnter={() => setFocusId(fk)}
                    onFocus={() => setFocusId(fk)}
                    whileHover={reduce ? undefined : { x: 2 }}
                    whileTap={reduce ? undefined : { scale: 0.99 }}
                    className={[
                      "flex w-full items-center gap-2 rounded-2xl border bg-white px-2 py-1.5 text-left shadow-sm transition-[border-color,box-shadow]",
                      "outline-none focus-visible:ring-2 focus-visible:ring-stone-900/20 focus-visible:ring-offset-1",
                      focusId === fk ? "border-stone-400 ring-1 ring-stone-300" : "border-stone-200/80 hover:border-stone-300/90",
                    ].join(" ")}
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-stone-100">
                      <User className="h-3.5 w-3.5 text-stone-500" />
                    </div>
                    <div className="min-w-0 text-left text-[10px] sm:text-xs">
                      <p className="font-bold text-stone-900">{u.name}</p>
                      <p className="text-stone-500">{u.role}</p>
                    </div>
                  </motion.button>
                );
              })}
              <motion.button
                type="button"
                onMouseEnter={() => setFocusId("enter")}
                onFocus={() => setFocusId("enter")}
                whileHover={reduce ? undefined : { scale: 1.02 }}
                whileTap={reduce ? undefined : { scale: 0.98 }}
                className="mt-0.5 w-full rounded-2xl border border-stone-200/90 bg-white py-2.5 text-center text-xs font-bold text-stone-900 shadow-sm transition hover:border-stone-300/90 hover:shadow"
              >
                Enter app
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>

      <p id={hintId} className="mt-3 min-h-[2.75rem] text-center text-xs leading-relaxed text-stone-500 sm:text-sm" aria-live="polite">
        <AnimatePresence mode="wait">
          <motion.span
            key={focusId ?? "default"}
            initial={reduce ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -4 }}
            transition={{ duration: 0.2, ease }}
            className="block"
          >
            {hint}
          </motion.span>
        </AnimatePresence>
      </p>
    </div>
  );
}
